// ============================================================
// Press coverage registry
// ============================================================
// Single source of truth for /company/press.
//
// Four sections:
//   essays    — pieces authored by Andrew, published on external platforms
//   podcasts  — podcast / audio / video appearances
//   speaking  — keynotes, panels, guest lectures, workshops, community events
//   coverage  — third-party media writing about QCoda
//
// Add entries newest-first within each array.
// `date` is ISO (YYYY-MM-DD) for structured data; `dateDisplay` is human-readable.

export const essays = [
    {
        title: 'Alignment-Driven Development',
        description:
            'The orchestration framework AI-paired coding forgot to build.',
        href: 'https://hackernoon.com/alignment-driven-development-the-orchestration-framework-ai-paired-coding-forgot-to-build',
        source: 'HackerNoon',
        date: '2025-01-01', // update with actual date if known
        dateDisplay: '2025',
        image: '/blog/alignment-driven-development.jpg',
        imageAlt: 'A conductor leading an orchestra of musicians with glowing lightbulbs for heads.',
        internal: false,
    },
    {
        title: 'Why Small Models Matter in a Network of Experts Era',
        description:
            'Distributed Network of Experts — API-linked large and small AI models to drive innovation and accuracy.',
        href: 'https://hackernoon.com/why-small-models-matter-in-a-network-of-experts-era',
        source: 'HackerNoon',
        date: '2024-01-01', // update with actual date if known
        dateDisplay: '2024',
        image: '/blog/why-small-models-matter.jpg',
        imageAlt: 'Abstract network diagram showing large and small AI model nodes linked across a distributed inference graph.',
        internal: false,
    },
    {
        title: 'We Built a Local Model Arena in 30 Minutes — Infrastructure Mattered More Than the App',
        description:
            'A hands-on tutorial building a Model Arena app that pits local LLMs against each other through LLMesh.',
        href: 'https://hackernoon.com/we-built-a-local-model-arena-in-30-minutes-infrastructure-mattered-more-than-the-app',
        source: 'HackerNoon',
        date: '2024-01-01', // update with actual date if known
        dateDisplay: '2024',
        image: '/blog/local-model-arena.jpg',
        imageAlt: 'Two AI model robots facing off in an arena, surrounded by infrastructure and server racks.',
        internal: false,
    },
    {
        title: 'Streaming Faster Made Our LLM Hub Slower',
        description:
            'One POST per LLM token kills multi-user throughput. Here\'s the 258-line adaptive batcher that fixed it.',
        href: 'https://hackernoon.com/streaming-faster-made-our-llm-hub-slower',
        source: 'HackerNoon',
        date: '2024-01-01', // update with actual date if known
        dateDisplay: '2024',
        image: '/blog/streaming-faster-llm-hub.jpg',
        imageAlt: 'A data stream visualization showing token throughput with a bottleneck resolved by an adaptive batcher.',
        internal: false,
    },
];

// Podcast and audio/video appearances. Add newest first.
export const podcasts = [
    {
        title: 'Writing, Interneting, and Existing in The Age of AI',
        show: 'HackerNoon',
        description: 'Andrew Schwabe discusses AI, modern technology, and the evolving landscape of writing and the internet.',
        href: 'https://www.youtube.com/watch?v=-SlFLu-fCO4',
        date: '2025-08-29',
        dateDisplay: 'August 2025',
    },
    {
        title: 'The Disruption Is Now: Innovating with Behavioral Data Science',
        show: 'Behavioral Data Science',
        description: 'A conversation on behavioral data science, disruption, and the practical application of AI in business.',
        href: 'https://www.youtube.com/watch?v=EZuClHUFhnU',
        date: '2024-10-02',
        dateDisplay: 'October 2024',
    },
    {
        title: 'CEO TALK: Stay Ahead with AI',
        show: 'MMA ALC Summit 2024',
        description: 'Andrew Schwabe on staying ahead with AI at the MMA Asia Leadership Congress.',
        href: 'https://www.youtube.com/watch?v=E_orbmUeGx4',
        date: '2024-08-22',
        dateDisplay: 'August 2024',
    },
    {
        title: 'Demystifying A.I. for Business People',
        show: 'Tectonic Lounge',
        description: 'Practical insights from Andrew Schwabe on making AI accessible and actionable for business leaders.',
        href: 'https://www.youtube.com/watch?v=6wlYunvuYFc',
        date: '2024-07-23',
        dateDisplay: 'July 2024',
    },
    {
        title: 'People Are Starting to Wake Up and Say "Service Matters"',
        show: 'Asia Insurtech Podcast',
        description: 'Andrew Schwabe on customer-centric technology and the importance of service in the insurtech landscape.',
        href: 'https://asiainsurtechpodcast.com/ep-181-andrew-schwabe-founder-chairman-at-formotiv-people-are-starting-to-wake-up-and-say-service-matters/',
        date: '2022-08-15',
        dateDisplay: 'August 2022',
    },
    {
        title: 'Building Up an Irresistible Culture',
        show: 'FUSE Podcast',
        description: 'Andrew Schwabe on product-market fit, team culture, and building technology companies in Vietnam.',
        href: 'https://www.youtube.com/watch?v=SAIinfRphlI',
        date: '2021-11-26',
        dateDisplay: 'November 2021',
    },
];

// Speaking engagements, keynotes, panels, guest lectures, workshops, and community events.
// Add newest first.
export const speaking = [
    {
        title: 'RTD 2026 – FutureScape: Keynote at UEH Mekong',
        venue: 'UEH Mekong',
        description: 'Andrew Schwabe delivers the keynote on AI and the future of technology at UEH Mekong.',
        href: 'https://www.linkedin.com/posts/saigonai_saigonai-aicompanyinvietnam-ueh-activity-7485227825705295872-15EF',
        date: '2026-07-17',
        dateDisplay: 'July 2026',
    },
    {
        title: 'HEX × Notion × Saigon A.I. × QCoda Collaboration Event',
        venue: 'HEX',
        description: 'A collaborative community event bringing together HEX, Notion, Saigon A.I., and QCoda.',
        href: 'https://www.linkedin.com/posts/saigonai_saigonai-hex-notion-activity-7482343240306688000-lURL',
        date: '2026-07-09',
        dateDisplay: 'July 2026',
    },
    {
        title: 'AmCham Vietnam Illuminate Series: AI in Education',
        venue: 'AmCham Vietnam',
        description: 'Speaking on the role of artificial intelligence in education at the AmCham Illuminate Series.',
        href: 'https://www.linkedin.com/posts/amcham-vietnam_amchamvietnam-illuminateseries-aiineducation-activity-7468522605487013890-ltOx',
        date: '2026-06-05',
        dateDisplay: 'June 2026',
    },
    {
        title: 'Code Engine Studio Corporate Training: AI for Business',
        venue: 'Code Engine Studio',
        description: 'Corporate training session on building successful AI projects and avoiding common pitfalls.',
        href: 'https://www.linkedin.com/posts/codeenginestudio_ai-is-everywhere-but-building-something-activity-7445862723340566529-J5rY',
        date: '2026-03-19',
        dateDisplay: 'March 2026',
    },
    {
        title: 'International Business Study Trip: Cambridge EMBA Programme',
        venue: 'Cambridge EMBA',
        description: 'Hosting Cambridge EMBA students for an international business study session on AI in Vietnam.',
        href: 'https://www.linkedin.com/feed/update/urn:li:activity:7444228482806763520',
        date: '2026-03-25',
        dateDisplay: 'March 2026',
    },
    {
        title: 'Beyond the Hype: Decoding AI\'s Impact on the Tech Workforce',
        venue: 'Vietcaps',
        description: 'Ecosystem dynamics, future demand, and cutting through AI hype for the Vietnamese tech workforce.',
        href: 'https://www.linkedin.com/feed/update/urn:li:activity:7429328771821985792',
        date: '2026-01-04',
        dateDisplay: 'January 2026',
    },
    {
        title: 'VCCI In-Depth Training: AI and Digital Transformation',
        venue: 'VCCI',
        description: 'In-depth training course on AI adoption and digital transformation for Vietnamese businesses.',
        href: 'https://www.linkedin.com/feed/update/urn:li:activity:7409035043404443648',
        date: '2025-12-10',
        dateDisplay: 'December 2025',
    },
    {
        title: 'UEH Innovation with Technology Guest Lecture',
        venue: 'UEH',
        description: 'Guest lecture on entrepreneurship, innovation, and AI in education.',
        href: 'https://www.linkedin.com/posts/joyrebecca_entrepreneurship-innovation-aieducation-activity-7388874690385203200--Yeh',
        date: '2025-10-16',
        dateDisplay: 'October 2025',
    },
    {
        title: 'Launch JDI: The Future of Artificial Intelligence — Chapter 3',
        venue: 'Launch JDI',
        description: 'Speaking on the future of AI at JDI\'s flagship technology event series.',
        href: 'https://www.linkedin.com/posts/launch-jdi_jdi-launchjdi-tfoai3-activity-7363878384310865925-GmBi',
        date: '2025-08-20',
        dateDisplay: 'August 2025',
    },
    {
        title: 'TEDxVNUIS 2025: Scarborne, Starborn',
        venue: 'TEDx',
        description: 'Andrew Schwabe takes the TEDx stage at VNUIS.',
        href: 'https://www.facebook.com/share/p/14FpaU2r9u2/',
        date: '2025-07-19',
        dateDisplay: 'July 2025',
    },
    {
        title: 'Engineered Prompting: Precision GenAI Outputs',
        venue: 'Saigon A.I.',
        description: 'Workshop on precision prompt engineering for enterprise-grade generative AI outputs.',
        href: 'https://www.linkedin.com/posts/saigonai_aitransformation_aiforleaders_workshopinhcmc-activity-7344265528665915393-anSy',
        date: '2025-06-25',
        dateDisplay: 'June 2025',
    },
    {
        title: 'CEEC Business Session: Work Smarter with AI',
        venue: 'CEEC Vietnam',
        description: 'Andrew Schwabe on practical AI adoption strategies for business leaders.',
        href: 'https://www.linkedin.com/posts/ceecvn_ceecvietnam_aiforbusiness_digitaltransformation-activity-7340664525685211137-_AzL',
        date: '2025-06-17',
        dateDisplay: 'June 2025',
    },
    {
        title: 'Organization Readiness for AI',
        venue: 'Saigon A.I.',
        description: 'Workshop helping organizations assess and build readiness for AI transformation.',
        href: 'https://www.linkedin.com/feed/update/urn:li:activity:7302870471190974464',
        date: '2025-02-27',
        dateDisplay: 'February 2025',
    },
    {
        title: 'RMIT Guest Lecturer: Foundations of AI for STEM',
        venue: 'RMIT University',
        description: 'Guest lecture on the foundations of artificial intelligence for STEM students.',
        href: 'https://www.linkedin.com/posts/saigonai_saigon-ai-highlight-sharing-knowledge-activity-7275132724263198722-y8Yj',
        date: '2024-12-02',
        dateDisplay: 'December 2024',
    },
    {
        title: 'Launch JDI: The Future of Artificial Intelligence — Chapter 2',
        venue: 'Launch JDI',
        description: 'Continuing the conversation on the future of AI at JDI\'s flagship event.',
        href: 'https://jdi.group/event-recap-the-future-of-artificial-intelligence-chapter-2/',
        date: '2024-07-12',
        dateDisplay: 'July 2024',
    },
    {
        title: 'FORTEC 2024: Keynote Speaker',
        venue: 'FORTEC',
        description: 'Keynote address at FORTEC 2024 on AI innovation and enterprise adoption.',
        href: 'https://www.linkedin.com/posts/fortec-event_thats-a-wrap-for-fortec-2024-activity-7201553098727673857-CJTB',
        date: '2024-05-28',
        dateDisplay: 'May 2024',
    },
    {
        title: 'RMIT Guest Lecture: Digital Entrepreneurship',
        venue: 'RMIT University',
        description: 'Guest lecture on digital entrepreneurship and building tech companies in Southeast Asia.',
        href: 'https://www.facebook.com/photo.php?fbid=745893747736738&set=a.491826273143488&id=100069483584342',
        date: '2024-04-20',
        dateDisplay: 'April 2024',
    },
];

// Third-party media coverage of QCoda / Andrew Schwabe. Add newest first.
export const coverage = [
    {
        title: 'Import and Export to the US Market: Opportunities and Solutions with AI Technology',
        outlet: 'VOH',
        description: 'Andrew Schwabe featured on AI-driven opportunities for Vietnamese businesses entering the US market.',
        href: 'https://voh.com.vn/thong-tin-doanh-nghiep/xuat-nhap-khau-vao-thi-truong-hoa-ky-co-hoi-va-giai-phap-voi-cong-nghe-ai-553850.html',
        date: '2024-09-26',
        dateDisplay: 'September 2024',
    },
    {
        title: 'Top Technology And AI Innovators — Design The Next Stages Of Your Business',
        outlet: 'Forbes',
        description: 'Featured among top technology and AI innovators shaping the future of business.',
        href: 'https://www.forbes.com/sites/rebeccasuhrawardi/2021/10/08/thinking-about-ai-to-grow-your-fashion-or-design-brand-let-these-companies-inspire-you/',
        date: '2021-10-08',
        dateDisplay: 'October 2021',
    },
    {
        title: 'Meet the Women Data Analysts Who Are Smashing Stereotypes in Vietnam',
        outlet: 'YORK.IE',
        description: 'Coverage of Saigon A.I.\'s work building inclusive data science teams in Vietnam.',
        href: 'https://fuel.york.ie/news/data-ai/women-data-analysts-smashing-stereotypes-vietnam',
        date: '2021-04-21',
        dateDisplay: 'April 2021',
    },
];
